import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import EditIcon from '@mui/icons-material/Edit';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import {
  useGetStockLevelsQuery,
  useAdjustStockMutation,
  useUpdateReorderPointMutation,
  useGetLocationsQuery,
  useCreateLocationMutation,
} from '../../../store/api/inventoryApi';
import { useGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function InventoryDashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [outOfStockOnly, setOutOfStockOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustQuantity, setAdjustQuantity] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [reorderStock, setReorderStock] = useState(null);
  const [reorderPointValue, setReorderPointValue] = useState('');

  const { data: stockData, refetch } = useGetStockLevelsQuery({
    locationId: selectedLocation || undefined,
    lowStock: lowStockOnly && !outOfStockOnly ? true : undefined,
    outOfStock: outOfStockOnly || undefined,
    page,
    limit,
    search: searchTerm || undefined,
  });
  const { data: locations } = useGetLocationsQuery();
  const { data: productsData } = useGetProductsQuery({ limit: 1000 });
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const [adjustStock] = useAdjustStockMutation();
  const [updateReorderPoint] = useUpdateReorderPointMutation();
  const [createLocation] = useCreateLocationMutation();

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const handleAdjustStock = async () => {
    try {
      await adjustStock({
        productId: selectedProduct.id,
        locationId: selectedProduct.locationId,
        quantity: parseFloat(adjustQuantity),
        notes: adjustNotes,
      }).unwrap();
      refetch();
      setAdjustDialogOpen(false);
      setSelectedProduct(null);
      setAdjustQuantity('');
      setAdjustNotes('');
    } catch (error) {
      alert(error?.data?.message || 'Error adjusting stock');
    }
  };

  const openReorderDialog = (stock, product) => {
    setReorderStock({ ...stock, productName: product?.name });
    setReorderPointValue(stock.reorderPoint != null ? String(stock.reorderPoint) : '0');
    setReorderDialogOpen(true);
  };

  const handleSaveReorderPoint = async () => {
    if (!reorderStock) return;
    try {
      const value = parseFloat(reorderPointValue);
      await updateReorderPoint({
        id: reorderStock.id,
        reorderPoint: Number.isFinite(value) && value >= 0 ? value : 0,
      }).unwrap();
      refetch();
      setReorderDialogOpen(false);
      setReorderStock(null);
      setReorderPointValue('');
    } catch (error) {
      alert(error?.data?.message || 'Error updating reorder point');
    }
  };

  const stockLevels = stockData?.stockLevels || [];
  const pagination = stockData?.pagination;

  const filteredStock = stockLevels;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Inventory Management
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Search Products"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            select
            label="Location"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <MenuItem value="">All Locations</MenuItem>
            {locations?.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              setLowStockOnly(!lowStockOnly);
              if (lowStockOnly) setOutOfStockOnly(false);
            }}
            startIcon={<WarningIcon />}
            color={lowStockOnly ? 'warning' : 'primary'}
          >
            {lowStockOnly ? 'Show All' : 'Low Stock Only'}
          </Button>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              setOutOfStockOnly(!outOfStockOnly);
              if (outOfStockOnly) setLowStockOnly(false);
            }}
            startIcon={<RemoveShoppingCartIcon />}
            color={outOfStockOnly ? 'error' : 'primary'}
          >
            {outOfStockOnly ? 'Show All' : 'Out of Stock Only'}
          </Button>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reserved</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Available</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Avg Cost (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Stock Value (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reorder Point</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStock.length > 0 ? (
                  filteredStock.map((stock, index) => {
                    const product = productsData?.products?.find((p) => p.id === stock.productId);
                    const available = parseFloat(stock.quantity) - parseFloat(stock.reservedQuantity);
                    const rp = parseFloat(stock.reorderPoint);
                    const globalThreshold = Number(company?.lowStockThreshold ?? 10);
                    const effectiveThreshold = Number.isFinite(rp) && rp > 0 ? rp : globalThreshold;
                    const isLowStock = available <= effectiveThreshold;
                    const isOutOfStock = available <= 0;
                    return (
                      <TableRow key={stock.id} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {product?.name || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          {product?.brand || '-'}
                        </TableCell>
                        <TableCell>{product?.sku || '-'}</TableCell>
                        <TableCell>{stock.location?.name || '-'}</TableCell>
                        <TableCell>{stock.quantity}</TableCell>
                        <TableCell>{stock.reservedQuantity}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: isLowStock ? 'error.main' : 'success.main' }}>
                          {available}
                        </TableCell>
                        <TableCell>{formatAmount(stock.fifoAvgCost)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatAmount(stock.fifoValue)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography component="span">{stock.reorderPoint}</Typography>
                            <Button
                              size="small"
                              variant="text"
                              sx={{ minWidth: 0, p: 0.25 }}
                              onClick={() => openReorderDialog(stock, product)}
                              title="Set reorder point"
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {isOutOfStock ? (
                            <Chip label="Out of stock" color="error" size="small" />
                          ) : isLowStock ? (
                            <Chip label="Low Stock" color="warning" size="small" />
                          ) : (
                            <Chip label="In Stock" color="success" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedProduct({
                                id: stock.productId,
                                locationId: stock.locationId,
                                name: product?.name,
                              });
                              setAdjustDialogOpen(true);
                            }}
                          >
                            Adjust
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No stock levels found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {pagination && pagination.pages > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={(pagination.page || 1) - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              rowsPerPage={pagination.limit || limit}
              onRowsPerPageChange={(e) => {
                const nextLimit = parseInt(e.target.value, 10) || 25;
                setLimit(nextLimit);
                setPage(1);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 2 }}
            />
          )}
        </CardContent>
      </Card>

      {/* Set Reorder Point Dialog */}
      <Dialog open={reorderDialogOpen} onClose={() => setReorderDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set Reorder Point</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {reorderStock && (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Product: {reorderStock.productName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Location: {reorderStock.location?.name}
                </Typography>
                <TextField
                  fullWidth
                  label="Reorder point"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={reorderPointValue}
                  onChange={(e) => setReorderPointValue(e.target.value)}
                  helperText="When stock falls to or below this level, it will show as Low Stock"
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReorderDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveReorderPoint} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={adjustDialogOpen} onClose={() => setAdjustDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Stock</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Product: {selectedProduct?.name}
            </Typography>
            <TextField
              fullWidth
              label="Adjustment Quantity"
              type="number"
              value={adjustQuantity}
              onChange={(e) => setAdjustQuantity(e.target.value)}
              helperText="Positive for increase, negative for decrease"
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={adjustNotes}
              onChange={(e) => setAdjustNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAdjustStock} variant="contained">
            Adjust Stock
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InventoryDashboard;
